import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";

export default function ClientSearchSelect({
  clients = [],
  value = "",
  onChange,
  placeholder = "Select client...",
  className = "",
  disabled = false,
  allowClear = true,
  searchPlaceholder = "Search by client name or code...",
  emptyText = "No client found",
  noOptionsText = "No clients",
  clearOnEmptySearch = false,
}) {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const normalizeSearchValue = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase();

  const getAllSearchableValues = (obj) => {
    if (!obj || typeof obj !== "object") return [];

    return Object.values(obj).flatMap((value) => {
      if (value === null || value === undefined) return [];

      if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        return [String(value)];
      }

      if (Array.isArray(value)) {
        return value.flatMap((item) => {
          if (
            typeof item === "string" ||
            typeof item === "number" ||
            typeof item === "boolean"
          ) {
            return [String(item)];
          }

          return [];
        });
      }

      return [];
    });
  };

  const normalizeClient = (client, index) => {
    const code =
      client?.us_code ??
      client?.user_code ??
      client?.userid ??
      client?.user_id ??
      client?.userId ??
      client?.client_code ??
      client?.code ??
      client?.value ??
      client?.id ??
      "";

    const name =
      client?.organization_name ??
      client?.restaurant_name ??
      client?.org_name ??
      client?.business_name ??
      client?.us_name ??
      client?.owner_name ??
      client?.user_name ??
      client?.name ??
      client?.label ??
      client?.title ??
      "";

    const rawStatus = normalizeSearchValue(
      client?.us_status ??
        client?.active_status ??
        client?.active ??
        client?.status ??
        "",
    );

    const statusText = ["1", "y", "yes", "active"].includes(rawStatus)
      ? "active"
      : ["0", "n", "no", "inactive"].includes(rawStatus)
        ? "inactive"
        : rawStatus;

    const searchableValues = [
      // Name
      name,
      client?.organization_name,
      client?.restaurant_name,
      client?.org_name,
      client?.business_name,

      // User / owner
      client?.us_name,
      client?.owner_name,
      client?.user_name,
      client?.first_name,
      client?.last_name,

      // Code / ID
      code,
      client?.us_code,
      client?.user_code,
      client?.userid,
      client?.user_id,
      client?.userId,
      client?.client_code,
      client?.fk_user_id,
      client?.id,

      // Contact
      client?.us_contact,
      client?.us_loginid,
      client?.mobile,
      client?.phone,
      client?.phone_no,
      client?.whatsapp_number,
      client?.email,

      // Gateway
      client?.payment_gateway,
      client?.paymentGateway,
      client?.gateway,

      // Status
      rawStatus,
      statusText,

      // Dates
      client?.us_last_login,
      client?.last_login,
      client?.created_at,
      client?.updated_at,

      // API ke baaki primitive fields bhi automatically
      ...getAllSearchableValues(client),
    ];

    return {
      raw: client,
      code: String(code),
      name: String(name),
      key: `${String(code || client?.id || "empty")}-${index}`,

      searchText: searchableValues
        .map(normalizeSearchValue)
        .filter(Boolean)
        .join(" "),
    };
  };

  const normalizedClients = useMemo(() => {
    return (clients || []).map(normalizeClient);
  }, [clients]);

  const selectedClient = useMemo(() => {
    return normalizedClients.find((c) => String(c.code) === String(value)) || null;
  }, [normalizedClients, value]);

 const filteredClients = useMemo(() => {
   const query = normalizeSearchValue(search);

   if (!query) return normalizedClients;

   const searchWords = query.split(/\s+/).filter(Boolean);

   return normalizedClients.filter((client) =>
     searchWords.every((word) => client.searchText.includes(word)),
   );
 }, [normalizedClients, search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
        setHighlightIndex(-1);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (search.trim()) {
      setHighlightIndex(filteredClients.length > 0 ? 0 : -1);
    } else {
      setHighlightIndex(-1);
    }
  }, [search, open, filteredClients.length]);

  useEffect(() => {
    if (!open || highlightIndex < 0) return;

    const el = listRef.current?.querySelector(`[data-index="${highlightIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, open]);

  useEffect(() => {
    if (!open) return;

    if (clearOnEmptySearch && search === "") {
      onChange?.(null);
    }
  }, [clearOnEmptySearch, onChange, open, search]);

  const handleSelect = (client) => {
    onChange?.(client?.raw ?? null);
    setOpen(false);
    setSearch("");
    setHighlightIndex(-1);
  };

  const handleClear = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onChange?.(null);
    setSearch("");
    setOpen(false);
    setHighlightIndex(-1);
  };

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const moveHighlight = (direction) => {
    if (!filteredClients.length) {
      setHighlightIndex(-1);
      return;
    }

    if (highlightIndex === -1) {
      setHighlightIndex(direction > 0 ? 0 : filteredClients.length - 1);
      return;
    }

    const next = highlightIndex + direction;
    if (next < 0) {
      setHighlightIndex(0);
      return;
    }
    if (next >= filteredClients.length) {
      setHighlightIndex(filteredClients.length - 1);
      return;
    }
    setHighlightIndex(next);
  };

  const handleButtonKeyDown = (e) => {
    if (disabled) return;

    if (!open) {
      if (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ") {
        e.preventDefault();
        openDropdown();
      }

      if ((e.key === "Backspace" || e.key === "Delete") && allowClear && String(value) !== "") {
        e.preventDefault();
        onChange?.(null);
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setSearch("");
      setHighlightIndex(-1);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveHighlight(1);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveHighlight(-1);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex === -1) return;
      const client = filteredClients[highlightIndex];
      if (client) handleSelect(client);
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setSearch("");
      setHighlightIndex(-1);
      return;
    }
  };

  const showClear = allowClear && !disabled && String(value) !== "";

  const buttonLabel = selectedClient
    ? selectedClient.code
      ? `${selectedClient.name} : ${selectedClient.code}`
      : selectedClient.name || placeholder
    : placeholder;

  return (
    <div ref={wrapperRef} className={`relative w-[320px] ${className}`}>
      <button
        type="button"
        onClick={toggleDropdown}
        onKeyDown={handleButtonKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-10 w-full items-center justify-between rounded-xl border bg-white px-3 text-sm outline-none ${
          disabled ? "cursor-not-allowed bg-gray-100 text-gray-400" : ""
        }`}
      >
        <span
          className={`min-w-0 truncate text-left ${
            selectedClient ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {buttonLabel}
        </span>

        <div className="ml-2 flex shrink-0 items-center gap-1">
          {showClear && (
            <span
              role="button"
              tabIndex={-1}
              title="Clear"
              aria-label="Clear selected client"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className="grid h-6 w-6 place-items-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={14} />
            </span>
          )}

          <ChevronDown
            size={16}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-xl border bg-white shadow-lg">
          <div className="border-b p-2">
            <div className="flex items-center gap-2 rounded-lg border px-2">
              <Search size={15} className="shrink-0 text-gray-400" />
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={searchPlaceholder}
                className="h-9 w-full bg-transparent text-sm outline-none"
              />
            </div>
          </div>

          <div ref={listRef} className="max-h-64 overflow-y-auto p-1" role="listbox">
            {normalizedClients.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">{noOptionsText}</div>
            ) : filteredClients.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">{emptyText}</div>
            ) : (
              filteredClients.map((client, index) => {
                const active = index === highlightIndex;
                const selected = String(client.code) === String(value);

                return (
                  <button
                    key={client.key}
                    data-index={index}
                    type="button"
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => handleSelect(client)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${
                      active ? "bg-[#01C37D]/10" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">
                        {client.name || "Client"}
                      </div>
                      {client.code ? (
                        <div className="text-xs text-gray-500">{client.code}</div>
                      ) : null}
                    </div>

                    {selected && (
                      <Check size={16} className="shrink-0 text-[#01C37D]" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}